import ErrorBoundary from '../../components/ErrorBoundary';
import 'bootstrap/dist/css/bootstrap.min.css';
import './style.css';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine, 
  faFileContract, 
  faUsers, 
  faBuilding, 
  faUserTie, 
  faHandshake,
  faCog,
  faChartBar,
  faFileAlt,
  faDatabase,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

// Interface para os tiles
interface LaunchpadTile {
  id: string;
  title: string;
  subtitle: string;
  icon: any;
  route: string;
  permissions: string[];
  isActive: boolean;
}

// Mapeamento de ícones
const iconMap: { [key: string]: any } = {
  'chart-line': faChartLine,
  'file-contract': faFileContract,
  'users': faUsers,
  'building': faBuilding,
  'user-tie': faUserTie,
  'handshake': faHandshake,
  'cog': faCog,
  'chart-bar': faChartBar,
  'file-alt': faFileAlt,
  'database': faDatabase
};

const Home = () => {
  const userName = localStorage.getItem('nome');
  const userPermissions = JSON.parse(localStorage.getItem('permissionsTest') || '[]');
  const navigate = useNavigate();
  
  const [tiles, setTiles] = useState<LaunchpadTile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Simulação de delay da API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulação de diferentes cenários baseado no usuário
      const userName = localStorage.getItem('nome') || '';
      let mockTiles = getMockTiles();
      
      // Simular diferentes permissões baseado no nome do usuário
      if (userName.toLowerCase().includes('admin')) {
        // Admin vê todos os tiles
        setTiles(mockTiles);
        // Configurar permissões de admin
        localStorage.setItem('permissionsTest', JSON.stringify([
          'cessoes.view', 'cessoes.manage', 'users.manage', 'cedentes.manage',
          'originadores.manage', 'fundos.manage', 'admin.manage', 'reports.view',
          'documents.manage', 'database.manage'
        ]));
      } else if (userName.toLowerCase().includes('gestor')) {
        // Gestor vê tiles de gestão
        setTiles(mockTiles);
        localStorage.setItem('permissionsTest', JSON.stringify([
          'cessoes.view', 'cessoes.manage', 'users.manage', 'cedentes.manage',
          'originadores.manage', 'fundos.manage', 'reports.view', 'documents.manage'
        ]));
      } else if (userName.toLowerCase().includes('operador')) {
        // Operador vê apenas operações
        setTiles(mockTiles);
        localStorage.setItem('permissionsTest', JSON.stringify([
          'cessoes.view', 'cessoes.manage', 'reports.view'
        ]));
      } else {
        // Usuário padrão vê apenas visualização
        setTiles(mockTiles);
        localStorage.setItem('permissionsTest', JSON.stringify([
          'cessoes.view', 'reports.view'
        ]));
      }
      
    } catch (err) {
      console.error('Erro ao buscar tiles:', err);
      setError('Erro ao carregar os tiles. Usando dados padrão.');
      setTiles(getMockTiles());
    } finally {
      setLoading(false);
    }
  };

  // Dados mock para fallback
  const getMockTiles = (): LaunchpadTile[] => [
    {
      id: 'cessoes-overview',
      title: 'Cessões - Overview',
      subtitle: 'Visão geral das operações',
      icon: faChartLine,
      route: '/aquisicao/operacoes/overview',
      permissions: ['cessoes.view'],
      isActive: true
    },
    {
      id: 'cessoes-operacoes',
      title: 'Cessões - Operações',
      subtitle: 'Gerenciar operações',
      icon: faFileContract,
      route: '/aquisicao/operacoes/',
      permissions: ['cessoes.manage'],
      isActive: true
    },
    {
      id: 'cadastro-usuarios',
      title: 'Cadastro de Usuários',
      subtitle: 'Gerenciar usuários do sistema',
      icon: faUsers,
      route: '/cadastros/usuario',
      permissions: ['users.manage'],
      isActive: true
    },
    {
      id: 'cadastro-cedentes',
      title: 'Cadastro de Cedentes',
      subtitle: 'Gerenciar cedentes',
      icon: faBuilding,
      route: '/cadastros/cedente',
      permissions: ['cedentes.manage'],
      isActive: true
    },
    {
      id: 'cadastro-originadores',
      title: 'Cadastro de Originadores',
      subtitle: 'Gerenciar originadores',
      icon: faUserTie,
      route: '/cadastros/originador',
      permissions: ['originadores.manage'],
      isActive: true
    },
    {
      id: 'cadastro-fundos',
      title: 'Cadastro de Fundos',
      subtitle: 'Gerenciar fundos',
      icon: faHandshake,
      route: '/cadastro/fundo',
      permissions: ['fundos.manage'],
      isActive: true
    },
    {
      id: 'cadastro-admin',
      title: 'Cadastro de Administradores',
      subtitle: 'Gerenciar administradores',
      icon: faCog,
      route: '/cadastros/admin',
      permissions: ['admin.manage'],
      isActive: true
    },
    {
      id: 'cessoes-operacoes',
      title: 'Cessões - Operações',
      subtitle: 'Visualizar lotes por dia',
      icon: faHandshake,
      route: '/aquisicao/operacoes',
      permissions: ['cessoes.view'],
      isActive: true
    },
    {
      id: 'documentos',
      title: 'Documentos',
      subtitle: 'Gerenciar documentos',
      icon: faFileAlt,
      route: '/documentos',
      permissions: ['documents.manage'],
      isActive: true
    },
    {
      id: 'base-dados',
      title: 'Base de Dados',
      subtitle: 'Configurações de dados',
      icon: faDatabase,
      route: '/base-dados',
      permissions: ['database.manage'],
      isActive: true
    }
  ];

  // Função para verificar se o usuário tem permissão para ver o tile
  const hasPermission = (tilePermissions: string[]): boolean => {
    if (!tilePermissions || tilePermissions.length === 0) return true;
    return tilePermissions.some(permission => userPermissions.includes(permission));
  };

  // Filtrar tiles baseado nas permissões do usuário
  // Se não há permissões configuradas, mostra todos os tiles ativos
  const filteredTiles = tiles.filter(tile => 
    tile.isActive && (userPermissions.length === 0 || hasPermission(tile.permissions))
  );

  useEffect(() => {
    fetchTiles();
  }, []);

  const handleTileClick = (route: string) => {
    navigate(route);
  };

  return (
    <ErrorBoundary>
      {/* Header simples */}
      <div className="page-header">
        <h1 className="page-title">My Home</h1>
        <p className="page-subtitle">Bem-vindo, {userName}</p>
        {error && (
          <div className="error-message">
            <small>{error}</small>
          </div>
        )}
      </div>

      {/* Launchpad Tiles */}
      <div className="launchpad-container">
        {loading ? (
          <div className="loading-container">
            <FontAwesomeIcon icon={faSpinner} className="spinner" />
            <p>Carregando tiles...</p>
          </div>
        ) : (
          <div className="launchpad-grid">
            {filteredTiles.length > 0 ? (
              filteredTiles.map((tile) => (
                <div
                  key={tile.id}
                  className="fiori-tile"
                  onClick={() => handleTileClick(tile.route)}
                >
                  <div className="tile-icon">
                    <FontAwesomeIcon icon={tile.icon} />
                  </div>
                  <div className="tile-content">
                    <h3 className="tile-title">{tile.title}</h3>
                    <p className="tile-subtitle">{tile.subtitle}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-tiles-message">
                <p>Nenhum tile disponível para seu perfil.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Home;